import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API } from '@app-core/constants/api.constants';
import { DEFAULT_DATA_RETENTION_DAYS, DEFAULT_DURATION_LIST } from '@app-core/constants/constants';
import { ClientConfigResponse, Duration, PortalNotificationEventFlag } from '@app-core/models/core.model';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

// todo - refactor portal notification settings in a separate service for better logical separation
@Injectable({
  providedIn: 'root',
})
export class DurationRangeService {
  private durationList: Duration[] = DEFAULT_DURATION_LIST;

  private _dataRetentionDays: number = DEFAULT_DATA_RETENTION_DAYS;

  private _includePolylinePathInfo: boolean = false;

  private _showPortalNotification: boolean = false;

  private _webRTCLiveStreamingEnabled: boolean = false;
  private _disableSuprsendNotification: boolean = false;
  private _portalNotificationEventFlags: PortalNotificationEventFlag[];
  private _planDetails;
  private _plan;
  private _reportsLogoS3Url;

  private _supportAsyncAssetReport: boolean;

  public get dataRetentionDays() {
    return this._dataRetentionDays;
  }

  public get showPortalNotification() {
    return this._showPortalNotification;
  }

  public get disableSuprsendNotification() {
    return this._disableSuprsendNotification;
  }

  public get planDetails() {
    return this._planDetails;
  }

  public get reportsLogoS3Url() {
    return this._reportsLogoS3Url;
  }
  public get plan() {
    return this._plan;
  }

  public get includePolylinePathInfo() {
    return this._includePolylinePathInfo;
  }

  public get portalNotificationEventFlags() {
    return this._portalNotificationEventFlags;
  }

  public get supportAsyncAssetReport() {
    return this._supportAsyncAssetReport;
  }

  public set dataRetentionDays(value: number) {
    this._dataRetentionDays = value;
    this.updateDurationList();
  }

  public set includePolylinePathInfo(value: boolean) {
    this._includePolylinePathInfo = value;
  }

  public set showPortalNotification(value) {
    this._showPortalNotification = value;
  }

  public get webRTCLiveStreamingEnabled() {
    return this._webRTCLiveStreamingEnabled;
  }

  public set portalNotificationEventFlags(value) {
    this._portalNotificationEventFlags = value;
  }
  public set disableSuprsendNotification(value) {
    this._disableSuprsendNotification = value;
  }
  constructor(private http: HttpClient) {}

  public getDurationList(): Duration[] {
    return this.durationList;
  }

  public updateDurationList() {
    this.durationList = this.getCustomDurationList(this.durationList, this.dataRetentionDays);
  }

  public getCustomDurationList(customDurationList: Duration[], dataRetentionPeriod = this.dataRetentionDays): Duration[] {
    let verifiedList = customDurationList.filter((item: Duration) => {
      if (item.days === 'custom' || item.days === 'yesterday') {
        return true;
      } else if (typeof item.days === 'number') {
        return item.days <= dataRetentionPeriod;
      }
      return false;
    });
    return verifiedList;
  }

  public getClientConfig(): Observable<ClientConfigResponse> {
    return this.http.get<ClientConfigResponse>(API.CLIENT_CONFIG).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      tap((res: ClientConfigResponse) => {
        const {
          dataRetention = DEFAULT_DATA_RETENTION_DAYS,
          includePolylinePathInfo = false,
          showPortalNotification = false,
          disableSuprsend = false,
          portalNotificationEventFlags,
          webRTCLiveStreamingEnabled = false,
          planDetails,
          plan,
          supportAsyncAssetReport = false,
          reportsLogoS3Url,
        } = res?.data || {};
        this.dataRetentionDays = dataRetention;
        this.includePolylinePathInfo = includePolylinePathInfo;
        this._webRTCLiveStreamingEnabled = webRTCLiveStreamingEnabled;
        this._showPortalNotification = showPortalNotification;
        this._disableSuprsendNotification = disableSuprsend;
        this._portalNotificationEventFlags = portalNotificationEventFlags;
        this._planDetails = planDetails || null;
        this._plan = plan;
        this._supportAsyncAssetReport = supportAsyncAssetReport;
        this._reportsLogoS3Url = reportsLogoS3Url;
        // dev testing
        // this._portalNotificationEventFlags = [
        //   'Cornering' , 'Harsh-Acceleration' , 'Harsh-Braking' ,
        //   'Cellphone-Distracted-Driving' , 'Smoking-Distracted-Driving' , 'Drinking-Distracted-Driving' ,
        //   'Distracted-Driving' , 'Lizard-Eye-Distracted-Driving' , 'Texting-Distracted-Driving' ,
        //   'Drowsy-Driving-Detected' , 'MaxSpeedExceeded' , 'Unbuckled-Seat-Belt' , 'Tail-Gating-Detected' , 'Forward-Collision-Warning' ,
        //   'Traffic-STOP-Sign-Violated' , 'Traffic-Light-Violated' , 'Lane-Drift-Found' ,
        //   'Custom-Triggered' , 'PotentialCrash' , 'DVRUpload' , 'Roll-Over-Detected'
        //   ];
      }),
      catchError(() => {
        this.dataRetentionDays = DEFAULT_DATA_RETENTION_DAYS;
        this.includePolylinePathInfo = false;
        this._showPortalNotification = false;
        this._portalNotificationEventFlags = [];
        this._supportAsyncAssetReport = false;
        return of(null);
      })
    );
  }
}
